import React, { useState, useEffect } from 'react';

interface ClassicFiscalPeriodProps {
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface FiscalPeriod {
    id: number;
    tenant_id: number;
    fiscal_year: number;
    start_date: string;
    end_date: string;
    is_current: boolean;
    is_finalized: boolean;
    created_at: string;
    updated_at: string;
}

const ClassicFiscalPeriod: React.FC<any> = ({
    apiFetch,
    currentTenantId,
}) => {
    const [periods, setPeriods] = useState<FiscalPeriod[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        fiscal_year: '',
        start_date: '',
        end_date: '',
        is_current: false,
        is_finalized: false,
    });

    // データ取得
    const fetchPeriods = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiFetch('/api/fiscal-periods');
            if (!response.ok) {
                throw new Error('会計期間の取得に失敗しました');
            }
            const data = await response.json();
            setPeriods(data);
        } catch (err: any) {
            setError(err.message || '会計期間の取得中にエラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentTenantId) {
            fetchPeriods();
        }
    }, [currentTenantId]);

    // 新規作成
    const handleCreate = async () => {
        try {
            setError(null);
            const response = await apiFetch('/api/fiscal-periods', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fiscal_year: parseInt(formData.fiscal_year),
                    start_date: formData.start_date,
                    end_date: formData.end_date,
                    is_current: formData.is_current,
                    is_finalized: formData.is_finalized,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '会計期間の作成に失敗しました');
            }

            await fetchPeriods();
            setIsCreating(false);
            setFormData({ fiscal_year: '', start_date: '', end_date: '', is_current: false, is_finalized: false });
        } catch (err: any) {
            setError(err.message || '会計期間の作成中にエラーが発生しました');
        }
    };

    // 更新
    const handleUpdate = async (id: number) => {
        try {
            setError(null);
            const response = await apiFetch(`/api/fiscal-periods/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    start_date: formData.start_date,
                    end_date: formData.end_date,
                    is_finalized: formData.is_finalized,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '会計期間の更新に失敗しました');
            }

            await fetchPeriods();
            setEditingId(null);
            setFormData({ fiscal_year: '', start_date: '', end_date: '', is_current: false, is_finalized: false });
        } catch (err: any) {
            setError(err.message || '会計期間の更新中にエラーが発生しました');
        }
    };

    // 現在期設定
    const handleSetCurrent = async (id: number) => {
        try {
            setError(null);
            const response = await apiFetch(`/api/fiscal-periods/${id}/set-current`, {
                method: 'PUT',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '現在期の設定に失敗しました');
            }

            await fetchPeriods();
        } catch (err: any) {
            setError(err.message || '現在期の設定中にエラーが発生しました');
        }
    };

    // 削除
    const handleDelete = async (id: number, fiscalYear: number) => {
        if (!window.confirm(`会計年度 ${fiscalYear} を削除してもよろしいですか？`)) {
            return;
        }

        try {
            setError(null);
            const response = await apiFetch(`/api/fiscal-periods/${id}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || '会計期間の削除に失敗しました');
            }

            await fetchPeriods();
        } catch (err: any) {
            setError(err.message || '会計期間の削除中にエラーが発生しました');
        }
    };

    // 編集開始
    const startEdit = (period: FiscalPeriod) => {
        setEditingId(period.id);
        setFormData({
            fiscal_year: period.fiscal_year.toString(),
            start_date: period.start_date,
            end_date: period.end_date,
            is_current: period.is_current,
            is_finalized: period.is_finalized,
        });
        setIsCreating(false);
    };

    // キャンセル
    const cancelEdit = () => {
        setEditingId(null);
        setIsCreating(false);
        setFormData({ fiscal_year: '', start_date: '', end_date: '', is_current: false, is_finalized: false });
    };

    return (
        <div style={{ padding: '16px', fontFamily: "'MS UI Gothic', sans-serif", fontSize: '11px' }}>
            <div style={{ marginBottom: '16px' }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '13px' }}>82: 会計期間設定</h2>
                <p style={{ margin: '0 0 16px 0', color: '#666' }}>
                    会計年度と期間の設定を行います（※管理者のみ）
                </p>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '8px 12px',
                    marginBottom: '16px',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                }}>
                    {error}
                </div>
            )}

            {/* 新規登録ボタン */}
            {!isCreating && !editingId && (
                <div style={{ marginBottom: '16px' }}>
                    <button
                        onClick={() => setIsCreating(true)}
                        className="classic-button"
                        style={{ padding: '6px 16px' }}
                    >
                        新規会計期間を追加
                    </button>
                </div>
            )}

            {/* 新規作成フォーム */}
            {isCreating && (
                <fieldset className="classic-fieldset" style={{ marginBottom: '16px' }}>
                    <legend className="classic-legend">新規会計期間</legend>
                    <div style={{ display: 'grid', gap: '12px', maxWidth: '500px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>
                                会計年度 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="number"
                                value={formData.fiscal_year}
                                onChange={(e) => setFormData({ ...formData, fiscal_year: e.target.value })}
                                style={{ width: '120px', padding: '4px' }}
                                placeholder="例: 2025"
                            />
                            <span style={{ marginLeft: '8px', color: '#666' }}>年度</span>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>
                                期首日 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="date"
                                value={formData.start_date}
                                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                style={{ padding: '4px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '4px' }}>
                                期末日 <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                style={{ padding: '4px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_current}
                                    onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })}
                                    style={{ marginRight: '6px' }}
                                />
                                現在期として設定
                            </label>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button
                                onClick={handleCreate}
                                className="classic-button"
                                disabled={!formData.fiscal_year || !formData.start_date || !formData.end_date}
                            >
                                登録
                            </button>
                            <button
                                onClick={cancelEdit}
                                className="classic-button"
                                style={{ backgroundColor: '#ddd' }}
                            >
                                キャンセル
                            </button>
                        </div>
                    </div>
                </fieldset>
            )}

            {/* 会計期間一覧 */}
            <fieldset className="classic-fieldset">
                <legend className="classic-legend">会計期間一覧</legend>

                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        読み込み中...
                    </div>
                ) : periods.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        会計期間が登録されていません
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '11px',
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#e8e8e8' }}>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'left' }}>会計年度</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>期首日</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>期末日</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', width: '80px' }}>現在期</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', width: '80px' }}>確定</th>
                                    <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', width: '200px' }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {periods.map((period) => (
                                    <tr
                                        key={period.id}
                                        style={{
                                            backgroundColor: period.is_current ? '#e8f4ff' : editingId === period.id ? '#ffffcc' : 'white'
                                        }}
                                    >
                                        {editingId === period.id ? (
                                            // 編集モード
                                            <>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                                                    {period.fiscal_year}年度
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <input
                                                        type="date"
                                                        value={formData.start_date}
                                                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                                        style={{ width: '120px', padding: '2px' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <input
                                                        type="date"
                                                        value={formData.end_date}
                                                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                                        style={{ width: '120px', padding: '2px' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {period.is_current ? '○' : '×'}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.is_finalized}
                                                        onChange={(e) => setFormData({ ...formData, is_finalized: e.target.checked })}
                                                    />
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => handleUpdate(period.id)}
                                                        className="classic-button"
                                                        style={{ marginRight: '4px', padding: '2px 8px', fontSize: '10px' }}
                                                    >
                                                        保存
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="classic-button"
                                                        style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: '#ddd' }}
                                                    >
                                                        キャンセル
                                                    </button>
                                                </td>
                                            </>
                                        ) : (
                                            // 表示モード
                                            <>
                                                <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                                                    {period.fiscal_year}年度
                                                    {period.is_current && (
                                                        <span style={{ marginLeft: '8px', color: '#0066cc', fontWeight: 'bold' }}>
                                                            【現在期】
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {period.start_date}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {period.end_date}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {period.is_current ? '○' : '×'}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {period.is_finalized ? '○' : '×'}
                                                </td>
                                                <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>
                                                    {!period.is_current && (
                                                        <button
                                                            onClick={() => handleSetCurrent(period.id)}
                                                            className="classic-button"
                                                            style={{ marginRight: '4px', padding: '2px 8px', fontSize: '10px' }}
                                                            disabled={editingId !== null}
                                                        >
                                                            現在期に設定
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => startEdit(period)}
                                                        className="classic-button"
                                                        style={{ marginRight: '4px', padding: '2px 8px', fontSize: '10px' }}
                                                        disabled={editingId !== null}
                                                    >
                                                        編集
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(period.id, period.fiscal_year)}
                                                        className="classic-button"
                                                        style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: '#fdd' }}
                                                        disabled={editingId !== null || period.is_current}
                                                        title={period.is_current ? '現在期は削除できません' : ''}
                                                    >
                                                        削除
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </fieldset>
        </div>
    );
};

export default ClassicFiscalPeriod;
